import React from "react";
import { Award, Users, Globe, Leaf } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { useSiteContent } from "@/context/SiteContentContext";

const VALUE_ICONS = [Users, Award, Leaf, Globe];

export default function About() {
  const c = useSiteContent("about");
  const VALUES = [1, 2, 3, 4].map((i) => ({
    icon: VALUE_ICONS[i - 1],
    title: c[`value_${i}_title`],
    text: c[`value_${i}_text`],
  }));
  const STATS = [
    { num: c.stat_1_num, label: c.stat_1_label },
    { num: c.stat_2_num, label: c.stat_2_label },
    { num: c.stat_3_num, label: c.stat_3_label },
    { num: c.stat_4_num, label: c.stat_4_label },
  ];
  return (
    <PageLayout>
      <Seo
        title="About Us — Family-run Tour Operator from Agra"
        description="A family from Agra quietly crafting India's finest journeys. IATO members, government-licensed guides, ethical wildlife and fair-wage partnerships."
      />
      <PageHero
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        subtitle={c.hero_subtitle}
        image={c.hero_image}
      />

      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="container-tti">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">{c.story_eyebrow}</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                {c.story_title}
                <br />
                <span className="italic text-deepred">{c.story_title_italic}</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-foreground/80 text-[17px] leading-relaxed font-light">
              <p>{c.story_para_1}</p>
              <p>{c.story_para_2}</p>
              <p>{c.story_para_3}</p>
              <div className="pt-4">
                <div className="font-serif text-2xl text-deepred">{c.story_signature}</div>
                <div className="text-xs uppercase tracking-[0.25em] text-saffron mt-1">{c.story_location}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream-100" data-testid="about-values">
        <div className="container-tti">
          <div className="text-center mb-16">
            <div className="eyebrow mb-4">{c.values_eyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{c.values_title}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cream-200 border border-cream-200">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-cream p-10 hover:bg-white transition-colors">
                <div className="w-12 h-12 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center mb-6">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl mb-3">{v.title}</h3>
                <p className="text-sm text-foreground/70 font-light leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-peacock text-white relative overflow-hidden" data-testid="about-stats">
        <div className="absolute inset-0 mandala-bg" />
        <div className="container-tti relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="eyebrow text-marigold mb-4">{c.numbers_eyebrow}</div>
              <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
                {c.numbers_title}
                <br />
                <span className="italic text-marigold">{c.numbers_title_italic}</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {STATS.map((s, i) => (
                <div key={i} className="border border-white/15 p-8">
                  <div className="font-serif text-5xl text-marigold">{s.num}</div>
                  <div className="text-xs uppercase tracking-[0.25em] text-white/70 mt-3">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
